/**
 * R14 — the painting already done, or still in progress, at a site and for
 * its dealer.
 *
 * The creation screen and every approval level, on the web and on the phone,
 * read the same two endpoints and render the same shape. The shape lives here,
 * once, so none of them can drift from the service that answers them
 * (backend m06 GeoLocationService.lastWorkAt / lastWorkForJob).
 */

/** Painting already done, or a job still on its way through approval or execution. */
export type SiteWorkStage = 'DONE' | 'IN_PROGRESS';

/** One earlier job, newest first. */
export interface SiteWorkEntry {
  readonly jobRequestId: string;
  readonly jobNumber: string | null;
  /** Business day the job was raised, YYYY-MM-DD. */
  readonly requestDate: string;
  readonly customerName: string;
  readonly dealerCode: string | null;
  readonly workTypeName: string | null;
  readonly brandingType: string | null;
  readonly brandingItem: string | null;
  readonly vendorName: string | null;
  readonly areaRequested: string;
  readonly areaUnit: string;
  readonly executedArea: string | null;
  readonly executedAreaUnit: string | null;
  readonly currentStatus: string;
  /** Null when that job carries no usable position. */
  readonly distanceMeters: number | null;
  /** Latest site photo of that job, or null when it has none. */
  readonly photoId: string | null;
  /** Raised for the same dealer. */
  readonly sameDealer: boolean;
  /** Within the tenant's same-site distance. */
  readonly withinRadius: boolean;
  readonly stage: SiteWorkStage;
  /** When it reached COMPLETED; null while in progress or when never recorded. */
  readonly completedAt: string | null;
  /** How long ago it was completed, as the server counted it to today's business date. */
  readonly doneAgo: { readonly years: number; readonly months: number; readonly days: number } | null;
  /** The approval step it waits at, and who holds it — null when nothing is pending. */
  readonly pendingStepName: string | null;
  readonly pendingRoleCode: string | null;
  readonly pendingUserName: string | null;
}

/** What the raiser was shown at creation, and whether he chose to go ahead. */
export interface SiteHistoryAcknowledgement {
  readonly acknowledged: boolean;
  readonly shownJobRequestIds: readonly string[];
  readonly recordedAt: string;
  readonly recordedByName: string | null;
}

export interface SiteHistory {
  /** The tenant's same-site distance the entries were matched within. */
  readonly radiusMeters: number;
  /** False when the site has no usable position, so only the dealer could be matched. */
  readonly located: boolean;
  /** Every earlier job within the distance, done and in progress — not only those listed. */
  readonly nearby: { readonly done: number; readonly inProgress: number };
  readonly entries: readonly SiteWorkEntry[];
  /** Present only for an existing job. */
  readonly acknowledgement?: SiteHistoryAcknowledgement | null;
}

/** Before the job exists: by the site's position and its dealer. */
export function siteHistoryAtPath(latitude: number, longitude: number, customerId: string): string {
  return `/v1/job-requests/site-history?latitude=${encodeURIComponent(String(latitude))}&longitude=${encodeURIComponent(String(longitude))}&customerId=${encodeURIComponent(customerId)}`;
}

/** For a job that exists: measured from its own site, itself left out. */
export function siteHistoryOfJobPath(jobRequestId: string): string {
  return `/v1/job-requests/${encodeURIComponent(jobRequestId)}/site-history`;
}

/** The site photo of an earlier job, served by M06 under the job's own right. */
export function siteWorkPhotoPath(entry: Pick<SiteWorkEntry, 'jobRequestId' | 'photoId'>): string | null {
  return entry.photoId
    ? `/v1/job-requests/${encodeURIComponent(entry.jobRequestId)}/photos/${encodeURIComponent(entry.photoId)}/content`
    : null;
}

/** "BTL / Wall Painting", falling back to the work type when no branding line exists. */
export function siteWorkBranding(entry: SiteWorkEntry): string {
  const branding = [entry.brandingType, entry.brandingItem].filter((x) => x && x.trim()).join(' / ');
  return branding || entry.workTypeName || '—';
}

type Translate = (key: string, vars?: Readonly<Record<string, string | number>>) => string;

/**
 * Where an earlier job stands, in one sentence: done and how long ago (the
 * server counted it; "3 years 4 months back"), or in progress and the approval
 * step it waits at, with whom. `onDay` writes the completion instant as the
 * tenant writes days. Shared so the web and the phone say exactly the same.
 */
export function siteWorkStageText(entry: SiteWorkEntry, t: Translate, onDay: (iso: string) => string): string {
  if (entry.stage === 'DONE') {
    if (!entry.completedAt || !entry.doneAgo) return t('siteHistory.stage.doneUnrecorded');
    const { years, months, days } = entry.doneAgo;
    const parts: string[] = [];
    if (years) parts.push(t('siteHistory.ago.years', { n: years }));
    if (months) parts.push(t('siteHistory.ago.months', { n: months }));
    // Days only matter while it is under a month old.
    if (!years && !months && days) parts.push(t('siteHistory.ago.days', { n: days }));
    const date = onDay(entry.completedAt);
    return parts.length
      ? t('siteHistory.stage.doneAgo', { ago: parts.join(' '), date })
      : t('siteHistory.stage.doneToday', { date });
  }
  if (!entry.pendingStepName) return t('siteHistory.stage.inProgress', { status: entry.currentStatus });
  return t('siteHistory.stage.pendingWith', {
    step: entry.pendingStepName,
    holder: entry.pendingUserName ?? entry.pendingRoleCode ?? '—',
  });
}
